import { Avatar, Card, CardContent, Theme, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { createStyles, withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { GlobalClassKeys } from "assets/jss/common-styles";
import { AppMessageBox } from "components/AppMessageBox";
import IconButtonClose from "components/Buttons/IconButtonClose";
import IconButtonDelete from "components/Buttons/IconButtonDelete";
import IconButtonWeb from "components/Buttons/IconButtonWeb";
import { RequestIndicator, SpinnerIndicator } from "components/CallServer";
import IconAdd from "components/Icons/IconAdd";
import MuiRegularButton from "components/MuiExtras/Button/MuiRegularButton";
import CardHeaderColor from "components/MuiExtras/Card/CardHeaderColor";
import MuiTextFieldColor from "components/MuiExtras/CustomInput/MuiTextFieldColor";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPagination from "components/MuiExtras/MuiPagination";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import { SystemRoles } from "models/ApiEnums";
import { MemberSuggestionDto, PagedResult } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import Session from "services/Session";
import MemberAutoComplete, { MemberAutoCompleteSearchParams } from "views/Member/Components/MemberAutoComplete";


/////////////////////////////////////////////////////////////////////////////////////

const MaxTakeRecords = 8;

enum FormMode { Search, Add }
enum GridMode { Normal, Expanded }

const styles = (theme: Theme) => createStyles({
    active: {
        color: theme.genie.icon.action.color
    }
});

interface IProps {
    topicId: string;
}

interface IState {
    dialog: any;

    grid: GridMode,
    mode: FormMode,
    mouseOver: number;
    searchText: string;

    lastError: any;
    list: PagedResult<MemberSuggestionDto>;
    listCycle: RequestLifeCycle;
    offset: number;
}

type Props = IProps & WithStyles<typeof styles> & WithSnackbar & WithTranslation;


/////////////////////////////////////////////////////////////////////////////////////

class TopicRequiredMembers extends React.Component<Props, IState> {
    private readonly = false;
    private searchRef: any = null;
    private addRef: any = null;
    private apiGetSuggestions = api.getTopicRequiredMembersSuggestions();

    ///
    constructor(props: Props) {
        super(props);

        this.readonly = !Session.hasRole(SystemRoles.TrainingManager);

        this.state = {
            dialog: null,

            grid: GridMode.Normal,
            mode: FormMode.Add,
            mouseOver: null,
            searchText: "",

            listCycle: RequestLifeCycle.Loading,
            list: null,
            lastError: null,
            offset: 0
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Topic.Components.TopicRequiredMembers." + r);
    }

    ///
    private loadData(offset: number) {

        const { searchText } = this.state;

        this.setState({ listCycle: RequestLifeCycle.Loading });

        if (this.state.grid == GridMode.Expanded) {

            api.getWhoCanAnswerTestSuggestions(this.props.topicId, searchText, offset, MaxTakeRecords)
                .then((result) => {
                    this.setState({ list: result, listCycle: RequestLifeCycle.None, offset: offset });
                });
        }
        else {

            api.getTopicRequiredMembers(this.props.topicId, searchText, offset, MaxTakeRecords)
                .then((result) => {
                    this.setState({ list: result, listCycle: RequestLifeCycle.None, offset: offset });
                })
                .catch((error) => {
                    this.setState({ list: null, lastError: error, listCycle: RequestLifeCycle.LoadFailed, offset: 0 });
                });
        }
    }

    ///
    private handleGridMode = () => {
        if (this.state.grid == GridMode.Normal) {
            this.setState({ grid: GridMode.Expanded, mode: FormMode.Search }, () => this.loadData(0));
        }
        else {
            this.setState({ grid: GridMode.Normal, mode: FormMode.Add }, () => this.loadData(0));
        }
    };

    ///
    private handleFilterSwitch = () => {
        this.setState({
            grid: GridMode.Normal,
            mode: this.state.mode == FormMode.Add ? FormMode.Search : FormMode.Add,
            searchText: ""
        }, () => {
            if (this.state.mode == FormMode.Search && this.searchRef) this.searchRef.focus();
            else if (this.state.mode == FormMode.Add && this.addRef) this.addRef.focus();
        });
    };

    ///
    private handleExitAddMode = () => {
    };

    ///
    private handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ searchText: e.currentTarget.value });
    };

    ///
    private handleFilterClear = () => {
        if (this.state.grid == GridMode.Normal) {
            this.setState({ searchText: "", mode: FormMode.Add }, () => {
                if (this.state.mode == FormMode.Add && this.addRef) this.addRef.focus();
            });
        }
        else {
            this.setState({ searchText: "", mode: FormMode.Search }, () => this.loadData(0));
        }
    };

    ///
    private handleAdd(item: MemberSuggestionDto[]) {
        const exitAdd = () => {
            this.setState({ listCycle: RequestLifeCycle.None, dialog: null });
        };

        const continueAdd = () => {
            this.setState({ listCycle: RequestLifeCycle.Posting, dialog: null });

            api.topicAddRequiredMember(this.props.topicId, item[0].key)
                .then((ok) => {
                    if (ok) this.loadData(this.state.offset);
                })
                .finally(() => {
                    exitAdd();
                });
        };


        this.setState({ listCycle: RequestLifeCycle.Posting });

        api.checkTopicMember(this.props.topicId, item[0].key)
            .then((isMember) => {

                if (isMember)
                    continueAdd();
                else {

                    this.setState({
                        listCycle: RequestLifeCycle.None,
                        dialog: (
                            <MuiConfirmation
                                message={this.res("ConfirmationAddMember")}
                                onClose={
                                    (result) => {
                                        if (result) continueAdd();
                                        else exitAdd();
                                    }
                                }
                            />
                        )
                    });
                }
            });
    }

    ///
    private handleDelete(item: MemberSuggestionDto) {

        const continueDelete = () => {
            this.setState({ listCycle: RequestLifeCycle.Posting, dialog: null });

            api.topicDeleteRequiredMember(this.props.topicId, item.key)
                .then((ok) => {
                    if (ok) this.loadData(0);
                })
                .finally(() => {
                    this.setState({ listCycle: RequestLifeCycle.None });
                });
        };

        this.setState({
            dialog: (
                <MuiConfirmation
                    message={this.props.t("Common.Question.ConfirmationDeleteOfMessage", { name: item.label })}
                    onClose={
                        (result) => {
                            if (result) continueDelete();
                            else this.setState({ dialog: null });
                        }
                    }
                />
            )
        });
    }

    ///
    private renderList() {
        const { list, listCycle } = this.state;

        return (
            <>
                <MuiContainer height={360}>
                    {
                        listCycle == RequestLifeCycle.Loading
                            ? <SpinnerIndicator />
                            : list == null
                                ? <AppMessageBox resource="LoadPageDataFailed" />
                                : list.total == 0
                                    ? <Typography variant="subtitle1">{this.res("EmptyData")}</Typography>
                                    : list.items.map((g, index) => {
                                        return (
                                            <Grid
                                                key={index}
                                                container
                                                spacing={1}
                                                justifyContent="space-between"
                                                alignItems="center"
                                                wrap="nowrap"
                                                className={this.state.mouseOver == index ? GlobalClassKeys.overElementBackground : null}
                                                onMouseEnter={() => this.setState({ mouseOver: index })}
                                                onMouseLeave={() => this.setState({ mouseOver: null })}
                                            >
                                                <Grid item>
                                                    <Avatar src={g.thumbnailUrl} />
                                                </Grid>
                                                <Grid item xs zeroMinWidth>
                                                    <Typography noWrap>
                                                        {g.label}
                                                    </Typography>
                                                </Grid>
                                                {
                                                    !this.readonly && g.canDelete &&
                                                    <Grid item>
                                                        <IconButtonDelete size="small" color="muted" onClick={() => this.handleDelete(g)} />
                                                    </Grid>
                                                }
                                            </Grid>

                                        );
                                    })
                    }
                </MuiContainer>
                <MuiContainer>
                    {
                        list != null && list.total > 0 && list.total > MaxTakeRecords &&
                        <MuiPagination
                            limit={MaxTakeRecords}
                            offset={this.state.offset}
                            total={this.state.list.total}
                            onClick={(e, offset) => this.loadData(offset)}
                        />
                    }
                </MuiContainer>
            </>
        );
    }

    ///
    private renderHeaderInput() {
        return (
            <Grid container={true} justifyContent={"space-between"} >
                <Grid xs>
                    {
                        this.state.mode == FormMode.Search
                            ? <MuiTextFieldColor
                                fullWidth
                                placeholder={this.props.t("Common.Input.Placeholder.SearchFor")}
                                inputColor="inherit"
                                underlineColor="inherit"
                                value={this.state.searchText}
                                onChange={this.handleFilterChange}
                                onEscape={this.handleFilterClear}
                                onEnter={() => this.loadData(0)}
                                endAdornment={
                                    this.state.searchText == ""
                                        ? null
                                        : <IconButtonClose color="inherit" size="small" onClick={this.handleFilterClear} />
                                }
                                inputRef={(ref) => this.searchRef = ref}
                            />
                            : <MemberAutoComplete
                                overModal
                                placeholder={this.res("AddMemberPlaceholder")}
                                escapeOnEmpty={true}
                                selectedItems={null}
                                inputColor={"warning"}
                                underlineColor={"warning"}
                                onChange={(data) => this.handleAdd(data)}
                                onEscape={this.handleExitAddMode}
                                onBlur={this.handleExitAddMode}
                                onCustomGetMembers={
                                    (p: MemberAutoCompleteSearchParams) => this.apiGetSuggestions.execute({
                                        topicId: this.props.topicId,
                                        searchText: p.searchText,
                                        offset: p.offset,
                                        take: p.take
                                    })
                                }
                                inputRef={(ref) => this.addRef = ref}
                            />
                    }
                </Grid>
            </Grid>
        );

    }

    ///
    componentDidMount() {
        this.loadData(0);
        if (this.addRef) this.addRef.focus();
    }

    ///
    render() {
        const { dialog, listCycle, grid } = this.state;

        return (
            <>
                {dialog}
                {dialog == null && listCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                <Card>

                    <CardHeaderColor color="primary"
                        title={
                            <Grid container={true} justifyContent={"space-between"} >
                                <Grid item xs>
                                    {
                                        grid == GridMode.Normal &&
                                        <MuiRegularButton
                                            color={this.state.mode == FormMode.Add ? "warning" : "inherit"}
                                            onClick={this.handleFilterSwitch}
                                            startIcon={<IconAdd />}
                                        >
                                            <span>{this.res("ButtonAddText")}</span>
                                        </MuiRegularButton>
                                    }

                                    {this.renderHeaderInput()}
                                </Grid>
                                <Grid>
                                    <IconButtonWeb
                                        color={this.state.grid == GridMode.Expanded ? "warning" : "inherit"}
                                        title={this.props.t("Views.User.Components.UserEditGroup.ButtonExpandTitle")}
                                        onClick={this.handleGridMode}
                                    />
                                </Grid>
                            </Grid>
                        }
                    />

                    <CardContent style={{ paddingBottom: 0 }} >
                        {this.renderList()}
                    </CardContent>

                </Card>
            </>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(styles)(TopicRequiredMembers)));